import { ConflictGroup } from '../../interfaces/ConflictGroup';
import { expand } from '../../logic//expand';
import { LayoutPositionDetails } from '../../interfaces/LayoutPositionDetails';
import { overlap } from '../overlap';
import { SurfaceItem } from '../../interfaces/SurfaceItem';

/**
 * The conflict item interface
 * @param item the surface item
 * @param layout the calculate layout for item in conflict group
 */
export interface ConflictItem {
    item: SurfaceItem;
    layout: LayoutPositionDetails;
}

/**
 * Resolves positions of individual items in conflict group
 * @param data items in conflict group to resolve, assumed to be in a single frame column already.
 * @param isRTL is RTL (right-to-left) layout
 */
export default function getConflictsLayout(data: SurfaceItem[], isRTL: boolean): ConflictItem[] {
    // Clone the array, as we'll be mutating it
    let ungroupedItems = data.slice();
    let columns: SurfaceItem[][] = getColumns(ungroupedItems);

    let results: ConflictItem[] = [];
    columns.forEach((columnItems, columnIndex) => {
        results = results.concat(
            columnItems.map(item => {
                return { item: item, layout: getItemLayout(item, isRTL, columns, columnIndex) };
            })
        );
    });

    return results;
}

/**
 * Returns left and right position of item depending from RTL settings
 * @param item the item
 * @param isRTL is RTL
 * @param columns conflict columns
 * @param itemColumnIndex item's starting column
 */
function getItemLayout(
    item: SurfaceItem,
    isRTL: boolean,
    columns: SurfaceItem[][],
    itemColumnIndex: number
): LayoutPositionDetails {
    let left = itemColumnIndex / columns.length;
    let right = 1 - getItemColumnSpan(item, columns, itemColumnIndex) / columns.length;
    return isRTL ? { left: right, right: left } : { left: left, right: right };
}

/**
 * Gets item column end - the last columns the item may expand without overlapping with items in next columns
 * @param item the item
 * @param columns the columns
 * @param itemColumnIndex item's starting column
 */
function getItemColumnSpan(
    item: SurfaceItem,
    columns: SurfaceItem[][],
    itemColumnIndex: number
): number {
    let nextColumnIndex = itemColumnIndex + 1;
    for (; nextColumnIndex < columns.length; nextColumnIndex++) {
        let nextColumnItems = columns[nextColumnIndex];
        for (let i = 0; i < nextColumnItems.length; i++) {
            let columnItem = nextColumnItems[i];
            if (overlap(item, columnItem)) {
                return nextColumnIndex;
            }
        }
    }

    return nextColumnIndex;
}

/**
 * Gets collection of columns as as an array of group items
 * @param items conflcting items
 * @returns collection of columns as an array of group items
 */
function getColumns(items: SurfaceItem[]): SurfaceItem[][] {
    let groupings: SurfaceItem[][] = [];
    let itemGroup: SurfaceItem[];
    // Get columns until collection of remaining conflicting items gets exhausted
    while ((itemGroup = getColumn(items)) != null) {
        groupings.push(itemGroup);
    }
    return groupings;
}

/**
 * Gets column as group item from collection of remaining conflicting items
 * @param items The remaining conflicting items
 */
function getColumn(items: SurfaceItem[]): SurfaceItem[] {
    if (items.length > 0) {
        let firstItem = items.shift();
        let group: ConflictGroup = {
            key: '',
            children: [firstItem],
            start: firstItem.start,
            end: firstItem.end,
        };

        // search for other items that are not overlapping with first item
        for (let i = 0; i < items.length; ) {
            let item: SurfaceItem = items[i];
            if (!overlap(item, group)) {
                group.children.push(item);
                expand(group, item);
                items.splice(i, 1);
            } else {
                i++;
            }
        }
        return group.children;
    } else {
        return null;
    }
}
